import { Entity } from '@api/api/entity-schema';
import { ThemeExposure } from '@api/api/theme-overview';
import { formatTicker } from '@api/mappers/formatTicker';
import { RevampTable, TableHeader } from '@design/components/table';
import { useTranslation } from 'react-i18next';

import { IndustryThemeRow } from './IndustryThemeRow';
import styles from './IndustryThemesTable.module.scss';
import type { IndustryThemeTimeframe } from './useIndustryThemeMetrics';
import { useIndustryThemesColumns } from './useIndustryThemesColumns';

export interface IndustryThemesTableProps {
  entity: Entity;
  themes: ThemeExposure[];
  timeframe: IndustryThemeTimeframe;
}

export const IndustryThemesTable = ({
  entity,
  themes,
  timeframe,
}: IndustryThemesTableProps) => {
  const { t } = useTranslation(['earnings']);
  const columns = useIndustryThemesColumns();

  return (
    <div className={styles.root} data-testid="industry-themes-table">
      <div className={styles.headerSection}>
        <p className={styles.title}>
          {t('earnings:peerComparison.industryThemes.title', {
            asset: formatTicker(entity),
          })}
        </p>
      </div>
      <div className={styles.tableWrapper}>
        <RevampTable>
          <thead>
            <tr>
              {columns.map(column => (
                <TableHeader
                  key={column.id}
                  alignment={column.alignment ?? 'left'}
                  isHoverEnabled={false}
                >
                  {column.header}
                </TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {themes.map(themeExposure => (
              <IndustryThemeRow
                key={themeExposure.theme.id}
                themeExposure={themeExposure}
                timeframe={timeframe}
                columns={columns}
              />
            ))}
          </tbody>
        </RevampTable>
      </div>
    </div>
  );
};
